import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import api from '../../lib/api';
import {
  Bell, X, CheckCheck, AlertTriangle, Ticket, Wrench,
  Package, Clock, Megaphone, Trash2, Eye, Activity, ShoppingCart,
  Shield, TrendingUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const typeConfig = {
  low_stock: { icon: Package, color: 'bg-amber-500', label: 'Stoc Scazut' },
  sla: { icon: Clock, color: 'bg-red-600', label: 'SLA' },
  revision: { icon: Wrench, color: 'bg-orange-500', label: 'Revizie' },
  push: { icon: Megaphone, color: 'bg-indigo-500', label: 'Anunt' },
  new_ticket: { icon: Ticket, color: 'bg-rose-500', label: 'Tichet Nou' },
  ticket_update: { icon: Activity, color: 'bg-blue-500', label: 'Tichet' },
  ticket_status_change: { icon: Activity, color: 'bg-blue-600', label: 'Status Tichet' },
  stock_change: { icon: Package, color: 'bg-emerald-500', label: 'Stoc' },
  pm_change: { icon: Wrench, color: 'bg-teal-500', label: 'Mentenanta' },
  material_request: { icon: ShoppingCart, color: 'bg-violet-500', label: 'Cerere' },
  dashboard_refresh: { icon: TrendingUp, color: 'bg-cyan-500', label: 'Dashboard' },
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'acum';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}z`;
};

const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [pushNotifications, setPushNotifications] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const dropdownRef = useRef(null);
  const [bellShake, setBellShake] = useState(false);
  const [hideRead, setHideRead] = useState(() => {
    try { return localStorage.getItem('maintix_notif_hide_read') === '1'; } catch { return false; }
  });

  // Close on outside click (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [countRes, listRes, pushRes] = await Promise.all([
        api.get('/notifications/count'),
        api.get('/notifications?limit=30'),
        api.get('/notifications/push?limit=30')
      ]);

      const alertCount = countRes.data.total || 0;
      const alerts = (listRes.data || []).map(n => ({
        id: n.id, type: n.type,
        title: n.title, message: n.message, severity: n.severity,
        timestamp: n.created_at, read: n.read || false, source: 'alert'
      }));
      // If hideRead is active, only show unread alerts
      setNotifications(hideRead ? alerts.filter(a => !a.read) : alerts);

      const pushItems = (pushRes.data || []).filter(p => !p.dismissed);
      setPushNotifications(pushItems);
      setUnreadCount(alertCount + pushItems.length);
    } catch {}
  }, [user, hideRead]);

  // Live event handler
  const addLiveEvent = useCallback((type, data) => {
    const evt = {
      id: `live-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      type,
      title: data.title || type,
      message: data.message || '',
      timestamp: data.timestamp || new Date().toISOString(),
      read: false,
      source: 'live',
    };
    setLiveEvents(prev => [evt, ...prev].slice(0, 30));
    setBellShake(true);
    setTimeout(() => setBellShake(false), 1500);
    // When new events arrive, reset hide-read mode so new alerts show
    if (hideRead) {
      setHideRead(false);
      try { localStorage.removeItem('maintix_notif_hide_read'); } catch {}
    }
  }, [hideRead]);

  // Real-time subscriptions
  const isSectionChief = user?.role === 'section_chief';

  useRealtimeEvent('new_ticket', (msg) => {
    const d = msg.data || {};
    addLiveEvent('new_ticket', {
      title: `Tichet Nou #${d.number || ''}`,
      message: `${d.title || ''} - ${d.equipment_name || ''}\nPrioritate: ${d.priority || 'normal'}${d.created_by_name ? `\nCreat de: ${d.created_by_name}` : ''}${d.location_name ? `\nLocatie: ${d.location_name}` : ''}`,
      timestamp: msg.timestamp,
    });
    fetchNotifications();
  });

  useRealtimeEvent('ticket_update', (msg) => {
    const d = msg.data || {};
    addLiveEvent('ticket_update', {
      title: `Tichet #${d.number || ''} - ${msg.action || 'actualizat'}`,
      message: `${d.title || ''}\nStatus: ${d.status || ''}${d.assigned_to_name ? `\nAsignat: ${d.assigned_to_name}` : ''}`,
      timestamp: msg.timestamp,
    });
    fetchNotifications();
  });

  // Section chiefs get dedicated ticket status change notifications
  useRealtimeEvent('ticket_status_change', (msg) => {
    const d = msg.data || {};
    addLiveEvent('ticket_status_change', {
      title: `Tichet #${d.number || ''} - ${d.status_label || d.status || 'actualizat'}`,
      message: `${d.title || ''}${d.equipment_name ? `\nEchipament: ${d.equipment_name}` : ''}${d.changed_by ? `\nDe: ${d.changed_by}` : ''}`,
      timestamp: msg.timestamp,
    });
    fetchNotifications();
  });

  // Section chiefs don't need stock, PM, or material request notifications
  useRealtimeEvent('stock_change', (msg) => {
    if (isSectionChief) return;
    const d = msg.data || {};
    addLiveEvent('stock_change', {
      title: msg.action_label || 'Modificare Stoc',
      message: `${d.article_name || d.name || ''} - ${msg.action || ''}`,
      timestamp: msg.timestamp,
    });
  });

  useRealtimeEvent('pm_change', (msg) => {
    if (isSectionChief) return;
    const d = msg.data || {};
    addLiveEvent('pm_change', {
      title: msg.action_label || 'Actualizare Mentenanta',
      message: `${d.equipment_name || d.title || ''} - ${msg.action || ''}`,
      timestamp: msg.timestamp,
    });
  });

  useRealtimeEvent('material_request', (msg) => {
    if (isSectionChief) return;
    const d = msg.data || {};
    addLiveEvent('material_request', {
      title: msg.action_label || 'Cerere Material',
      message: `${d.title || d.article_name || ''} - ${msg.action || ''}`,
      timestamp: msg.timestamp,
    });
  });

  useRealtimeEvent('push_notification', () => fetchNotifications());

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 120000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // ==================== ACTIONS ====================
  const dismissPush = async (notifId) => {
    try {
      await api.post(`/notifications/push/${notifId}/dismiss`);
      setPushNotifications(prev => prev.filter(p => p.id !== notifId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      toast.success('Notificare stearsa');
    } catch {}
  };

  const markOneRead = async (notifId) => {
    try {
      await api.post(`/notifications/mark-read/${notifId}`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markLiveRead = (id) => {
    setLiveEvents(prev => prev.map(e => e.id === id ? { ...e, read: true } : e));
  };

  const deleteLiveEvent = (id) => {
    setLiveEvents(prev => prev.filter(e => e.id !== id));
  };

  const markAllAsRead = async () => {
    try {
      await Promise.all([
        api.post('/notifications/mark-all-read'),
        api.post('/notifications/push/dismiss-all'),
      ]);
      setNotifications([]);
      setPushNotifications([]);
      setLiveEvents(prev => prev.map(e => ({ ...e, read: true })));
      setUnreadCount(0);
      setHideRead(true);
      try { localStorage.setItem('maintix_notif_hide_read', '1'); } catch {}
      toast.success('Toate notificarile marcate ca citite');
    } catch { toast.error('Eroare'); }
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications/clear-all');
      setNotifications([]);
      setPushNotifications([]);
      setLiveEvents([]);
      setUnreadCount(0);
      setHideRead(true);
      try { localStorage.setItem('maintix_notif_hide_read', '1'); } catch {}
      toast.success('Lista de notificari golita');
    } catch { toast.error('Eroare la golirea notificarilor'); }
  };

  // ==================== RENDER HELPERS ====================
  const getIcon = (type) => {
    const config = typeConfig[type];
    const Icon = config ? config.icon : Bell;
    return <Icon className="w-4 h-4" />;
  };

  const getColor = (type, severity) => {
    if (severity === 'critical') return 'bg-red-600 text-white';
    if (severity === 'warning') return 'bg-amber-500 text-white';
    const config = typeConfig[type];
    return config ? `${config.color} text-white` : 'bg-slate-200 text-slate-600';
  };

  // Build items
  const pushItems = pushNotifications.map(p => ({
    id: p.id, type: 'push',
    title: p.title, message: p.message, severity: p.severity || 'info',
    timestamp: p.created_at, read: false, source: 'push'
  }));

  const filteredItems = activeTab === 'live'
    ? liveEvents
    : activeTab === 'alerts'
    ? notifications
    : [
        ...liveEvents.filter(e => !e.read),
        ...pushItems,
        ...notifications.filter(n => !n.read),
        ...liveEvents.filter(e => e.read),
        ...notifications.filter(n => n.read),
      ];

  const totalUnread = unreadCount + liveEvents.filter(e => !e.read).length;

  const renderNotificationItem = (n) => {
    const isUnread = !n.read;
    return (
      <div
        key={n.id}
        className={`group px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-all ${
          isUnread ? 'bg-blue-50/60 border-l-[3px] border-l-blue-500' : 'border-l-[3px] border-l-transparent'
        }`}
        data-testid={`notification-item-${n.id}`}
      >
        <div className="flex items-start gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getColor(n.type, n.severity)}`}>
            {getIcon(n.type)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className={`text-xs font-semibold truncate ${isUnread ? 'text-slate-900' : 'text-slate-500'}`}>
                {n.title}
              </p>
              <span className="text-[9px] text-slate-400 flex-shrink-0">{timeAgo(n.timestamp)}</span>
            </div>
            <p className="text-[11px] text-slate-600 mt-0.5 whitespace-pre-line leading-relaxed line-clamp-3">
              {n.message}
            </p>
            {n.type && typeConfig[n.type] && (
              <span className={`inline-block text-[8px] font-bold uppercase tracking-wide mt-1 px-1.5 py-0.5 rounded ${getColor(n.type, n.severity)} opacity-80`}>
                {typeConfig[n.type].label}
              </span>
            )}
          </div>
          {/* Action buttons - always visible on mobile, hover on desktop */}
          <div className="flex flex-col gap-0.5 flex-shrink-0 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
            {isUnread && n.source === 'alert' && (
              <button
                onClick={(e) => { e.stopPropagation(); markOneRead(n.id); }}
                className="w-7 h-7 rounded-lg hover:bg-blue-100 flex items-center justify-center active:scale-90 transition-transform"
                title="Marcheaza ca citit"
                data-testid={`read-${n.id}`}
              >
                <Eye className="w-3.5 h-3.5 text-blue-500" />
              </button>
            )}
            {n.source === 'push' && (
              <button
                onClick={(e) => { e.stopPropagation(); dismissPush(n.id); }}
                className="w-7 h-7 rounded-lg hover:bg-red-100 flex items-center justify-center active:scale-90 transition-transform"
                title="Sterge"
                data-testid={`dismiss-${n.id}`}
              >
                <X className="w-3.5 h-3.5 text-red-500" />
              </button>
            )}
            {n.source === 'live' && (
              <>
                {isUnread && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markLiveRead(n.id); }}
                    className="w-7 h-7 rounded-lg hover:bg-blue-100 flex items-center justify-center active:scale-90 transition-transform"
                    title="Marcheaza ca citit"
                  >
                    <Eye className="w-3.5 h-3.5 text-blue-500" />
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); deleteLiveEvent(n.id); }}
                  className="w-7 h-7 rounded-lg hover:bg-red-100 flex items-center justify-center active:scale-90 transition-transform"
                  title="Sterge"
                >
                  <X className="w-3.5 h-3.5 text-red-500" />
                </button>
              </>
            )}
          </div>
          {isUnread && (
            <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0 animate-pulse" />
          )}
        </div>
      </div>
    );
  };

  // ==================== DROPDOWN CONTENT (shared between mobile & desktop) ====================
  const dropdownContent = (
    <>
      {/* Header */}
      <div className="bg-slate-900 text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            <h3 className="font-bold text-sm">Notificari</h3>
            {totalUnread > 0 && (
              <span className="bg-red-500 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{totalUnread}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setIsOpen(false)} className="lg:hidden w-8 h-8 rounded-lg hover:bg-slate-700 flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Action buttons - big and clear */}
        <div className="flex gap-2 mt-2">
          {totalUnread > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs font-medium transition-colors active:scale-95"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Marcheaza citite
            </button>
          )}
          {(filteredItems.length > 0 || totalUnread > 0) && (
            <button
              onClick={clearAll}
              className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-red-600/80 hover:bg-red-600 rounded-lg text-xs font-medium transition-colors active:scale-95"
              data-testid="clear-all-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Goleste tot
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-2">
          {[
            { key: 'all', label: 'Toate' },
            { key: 'live', label: 'Live', count: liveEvents.filter(e => !e.read).length },
            { key: 'alerts', label: 'Alerte', count: notifications.filter(n => !n.read).length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-colors ${
                activeTab === tab.key ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1 bg-red-500 text-white text-[9px] px-1 rounded-full">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filteredItems.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">Nicio notificare</p>
            <p className="text-xs mt-1 text-slate-300">Totul este in regula!</p>
          </div>
        ) : (
          filteredItems.map(renderNotificationItem)
        )}
      </div>

      {/* Footer */}
      {(filteredItems.length > 0 || hideRead) && (
        <div className="px-4 py-2 border-t bg-slate-50 flex items-center justify-between flex-shrink-0">
          <span className="text-[10px] text-slate-500">
            {liveEvents.filter(e => !e.read).length > 0 && <span className="text-emerald-600 font-medium">{liveEvents.filter(e => !e.read).length} live</span>}
            {pushNotifications.length > 0 && <> · {pushNotifications.length} push</>}
            {notifications.filter(n => !n.read).length > 0 && <> · {notifications.filter(n => !n.read).length} alerte</>}
          </span>
          <div className="flex items-center gap-2">
            {hideRead && (
              <button
                onClick={() => {
                  setHideRead(false);
                  try { localStorage.removeItem('maintix_notif_hide_read'); } catch {}
                  fetchNotifications();
                }}
                className="text-[10px] text-blue-500 hover:text-blue-700 font-medium"
                data-testid="show-all-btn"
              >
                Arata toate
              </button>
            )}
            <span className="text-[10px] text-slate-400">{filteredItems.length} total</span>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      {/* Bell Button */}
      <Button
        variant="ghost" size="icon" className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell-button"
      >
        <Bell className={`w-5 h-5 text-slate-600 ${bellShake ? 'animate-bounce' : ''}`} />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse" data-testid="notification-count">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </Button>

      {isOpen && (
        <>
          {/* MOBILE: Full-screen overlay */}
          <div className="lg:hidden fixed inset-0 z-50 bg-white flex flex-col" data-testid="notification-dropdown-mobile">
            {dropdownContent}
          </div>

          {/* DESKTOP: Dropdown */}
          <div className="hidden lg:flex lg:flex-col absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden max-h-[520px]" data-testid="notification-dropdown">
            {dropdownContent}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
